import { CommonModule } from '@angular/common';
import {Component, Injector, OnDestroy, OnInit} from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { goBack } from '../../../../common/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Cert } from '../../../../model/certs.model';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { Subject, forkJoin, takeUntil } from 'rxjs';
import {VenViewModel} from "../../../../model/vens.model";
import {HttpParams} from "@angular/common/http";
import {LoaderComponent} from "../../../common/loader/loader.component";

@Component({
  selector: 'app-create-cert-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, ReactiveFormsModule, LoaderComponent],
  templateUrl: './create-cert.component.html',
  styleUrl: './create-cert.component.scss'
})
export class CreateCertComponent implements OnInit, OnDestroy {
  protected isLoading: boolean = false;
  public secondaryCertToggle: boolean = false;
  public editing: boolean = false;
  public cert_id: number;
  public route: Router;
  public activatedRoute: ActivatedRoute;
  public editVens: any;
  public error: string | null = null;

  // form
  certForm = new FormGroup({
    vens: new FormControl<number>(0, [Validators.required]),
    publicCert: new FormControl<string>('', this.editing ? [] : [Validators.required]),
    privateKey: new FormControl<string>('', this.editing ? [] : [Validators.required]),
    secondaryPublicCert: new FormControl<string>(''),
    secondaryPrivateKey: new FormControl<string>(''),
  });
  unsubscribe = new Subject<void>();
  formsSubscribe: any;

  public vens: { id: number, venName: string }[] = [];

  public cert: Cert = {
    created_at: '',
    private_key: '',
    public_cert: '',
    secondary_private_key: '',
    secondary_public_cert: '',
    updated_at: '',
    updated_by: '',
    ven_id: 0,
    secondary_cert_fingerprint: '',
    cert_fingerprint: '',
    ven: {}
  };

  constructor(private injector: Injector, private certService: ApiService, private formBuilder: FormBuilder) {
    this.route = injector.get(Router);
    this.activatedRoute = injector.get(ActivatedRoute);

    // Use ActivatedRoute instead of Router event for more reliable parameter access
    this.activatedRoute.params.subscribe(params => {
      if (params['id']) {
        this.editing = true;
        this.cert_id = +params['id']; // Convert to number
      }
    });
  }

  setFormsDefaultValue(){
    this.certForm.controls.vens.setValue(this.editVens?.id);

    // Disable VEN selection when editing
    if(this.editing) {
      this.certForm.controls['vens'].disable();
    } else {
      this.certForm.controls['vens'].enable();
    }

    this.certForm.controls.publicCert.setValue(this.cert?.public_cert);
    this.certForm.controls.privateKey.setValue(this.cert?.private_key);
    this.certForm.controls.secondaryPublicCert.setValue(this.cert?.secondary_public_cert);
    this.certForm.controls.secondaryPrivateKey.setValue(this.cert?.secondary_private_key);

    // If secondary cert exists, expand the section
    if (this.cert?.secondary_public_cert || this.cert?.secondary_private_key) {
      this.secondaryCertToggle = true;
    }
  }

  ngOnInit(): void {
    this.certForm = this.formBuilder.group({
      vens:[0, [Validators.required]],
      publicCert: ['', this.editing ? [] : [Validators.required]],
      privateKey: ['', this.editing ? [] : [Validators.required]],
      secondaryPublicCert: [''],
      secondaryPrivateKey: [''],
    });

    const vensQueryParams: CustomHttpParams = {
      'depths': JSON.stringify({
        'cert': 1
      }),
    };
    const certQueryParams: CustomHttpParams = {
      'depth': 2,
      'ven_id': this.cert_id
    };
    this.isLoading = true;
    forkJoin([
      this.certService.findCert(certQueryParams).pipe(takeUntil(this.unsubscribe)),
      this.certService.findVens(vensQueryParams).pipe(takeUntil(this.unsubscribe))
    ]).subscribe({
      next: ([responseCert, responseVens]) => {
        if(responseCert) {
          this.cert = responseCert.find((cert: Cert) => cert.ven_id === Number(this.cert_id)) ?? this.cert;
          this.editVens = responseCert.find((cert: Cert) => cert.ven_id === this.cert?.ven_id)?.ven;
        }
        if(responseVens) {
          // If editing, show all VENs; if creating, filter to only show VENs without cert-index
          if (this.editing) {
            this.vens = responseVens.map((ven: VenViewModel) => ({id: ven.id, venName: ven.name }));
          } else {
            this.vens = responseVens
              .filter((ven: VenViewModel) => {
                // Check if this VEN doesn't already have a cert
                return !responseCert.some((cert: Cert) => {
                  return cert && cert.ven_id === ven.id
                });
              })
              .map((ven: VenViewModel) => ({id: ven.id, venName: ven.name }));
          }
        }
        this.setFormsDefaultValue();
        this.isLoading = false;
      },
      error: (err) => {
        console.error(err);
        this.error = "Failed to load data";
        this.isLoading = false;
      }
    });

    this.formsSubscribe = this.certForm.controls.vens.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.editVens = val ?? '';
        this.cert!.ven_id = val ?? '';
      }
    );

    this.formsSubscribe = this.certForm.controls.publicCert.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.cert!.public_cert = val ?? '';
      }
    );

    this.formsSubscribe = this.certForm.controls.privateKey.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.cert!.private_key = val ?? '';
      }
    );

    this.formsSubscribe = this.certForm.controls.secondaryPublicCert.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.cert!.secondary_public_cert = val ?? '';
      }
    );

    this.formsSubscribe = this.certForm.controls.secondaryPrivateKey.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(
      (val: any) => {
        this.cert!.secondary_private_key = val ?? '';
      }
    );
  }

  onImagePicked(event: any, name: string) {
    const file = event?.target.files[0];
    if (file) {
      let reader = new FileReader();
      reader.readAsText(file, "UTF-8");
      reader.addEventListener(
        "load",
        () => {
          this.certForm.patchValue({ [name]: reader.result });
        },
        false,
      );
      reader.addEventListener(
        "error",
        () => {
          this.error = "Error reading file.";
        },
        false
      );
    }
  }

  toggleSecondaryCerts() {
    this.secondaryCertToggle = !this.secondaryCertToggle;
  }

  save(){
    // Validate required fields
    if (!this.editing) {
      if (!this.cert.ven_id) {
        this.error = "VEN id is required.";
        return;
      }

      if (!this.cert.public_cert || !this.cert.private_key) {
        this.error = "No primary files uploaded, can't create cert-index.";
        return;
      }
    } else {
      // When editing, if one primary cert is uploaded, the other is required too
      if ((this.cert.public_cert && !this.cert.private_key) || (!this.cert.public_cert && this.cert.private_key)) {
        this.error = "Both public certificate and private key must be provided together.";
        return;
      }
    }

    // Validate both secondary certificates if either one is provided
    if (this.cert.secondary_public_cert || this.cert.secondary_private_key) {
      if (!this.cert.secondary_public_cert) {
        this.error = "Secondary public certificate is required when adding secondary certificates.";
        return;
      }
      if (!this.cert.secondary_private_key) {
        this.error = "Secondary private key is required when adding secondary certificates.";
        return;
      }
    }

    if (this.editing) {
      const updateData: any = {
        ven_id: this.cert.ven_id.toString()
      };

      if (this.cert.public_cert) updateData.public_cert = this.cert.public_cert;
      if (this.cert.private_key) updateData.private_key = this.cert.private_key;
      if (this.cert.secondary_public_cert) updateData.secondary_public_cert = this.cert.secondary_public_cert;
      if (this.cert.secondary_private_key) updateData.secondary_private_key = this.cert.secondary_private_key;

      this.certService.editCert(updateData).subscribe({
        next: () => {
          goBack('/certs', this.route);
        },
        error: (err) => {
          console.error(err);
          this.error = typeof err === 'string' ? err : "Failed to update certificate.";
        }
      });
    } else {
      // Prepare the cert object for creation
      const createData: any = {
        ven_id: this.cert.ven_id.toString(),
        public_cert: this.cert.public_cert,
        private_key: this.cert.private_key
      };

      // Only include secondary cert fields if they have values
      if (this.cert.secondary_public_cert) createData.secondary_public_cert = this.cert.secondary_public_cert;
      if (this.cert.secondary_private_key) createData.secondary_private_key = this.cert.secondary_private_key;

      this.certService.createCert(createData).subscribe({
        next: () => {
          goBack('/certs', this.route);
        },
        error: (err) => {
          console.error(err);
          this.error = typeof err === 'string' ? err : "Failed to create certificate.";
        }
      });
    }
  }

  backPage(url: string, router: Router){
    goBack(url, router);
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
